import React from "react";
import FallbackImage from "../FallbackImage";
import { ISearchCardProps } from "../interfaces";
import { IAddToCartData, ToastStore } from "../stores/ToastStore";
import SVG from "../SVG";
import { addToBookmarks, ICart, removeFromBookmarks, updateCart } from "../api";
import { Globals, t } from "../utils";
import { trackAddToCart } from "../tracking";
import { Button } from "../components/Button";

const { useState } = React;

// const videoFormats = ["MP4", "MPG", "WMV", "MOV", "MP3", "AVI"];

const SearchCard = ({
	elementType,
	title,
	image,
	productCategory,
	text,
	// style,
	isLoading,
	chips,
	url,
	id,
	isOnWishlist,
	icon,
	fallbackImage,
	downloadUrl,
	startDate,
	endDate,
	onlineLocation,
	purchasableId,
	price,
	priceAsCurrency,
    ctaButtonLinkText,
    cardBackgroundColor,
    cardBackgroundImage,
    cardSubtitleColor,
	trackingData,
	canAddToCart,
	isDigitalProduct,
	isNew,
	isApp,
	subTitle,
	subtitle,
	relatedProducts,
}: ISearchCardProps) => {
	const [isBookmarked, setIsBookmarked] = useState(isOnWishlist);
	// const searchCardClassName = isEvent
	// 	? "search-card search-card__event"
	// 	: "search-card";
	const isEvent = elementType == "events";
	const isTeachingSeries = elementType == "teachingSeries";
	const isMagazine = elementType == "magazineArticle";
	const isApaArticle = elementType == "apaArticle";
	const apaArticleUserMenu = elementType == "apaArticleUserMenu";
	const isLandingPage = elementType == "pages" || elementType == "schoolTypeLandingPage";
	const isDigitalAsset = elementType == "digitalAssets";

	const getFormattedDateTime = (date: Date) => {
		if (!date) return;

		const dateTimeObj: { date: string | null; time: string | null } = {
			date: null,
			time: null,
		};

		dateTimeObj["date"] = new Intl.DateTimeFormat("de-AT").format(date);

		const timeOptions: Intl.DateTimeFormatOptions = {
			hour: "numeric",
			minute: "numeric",
			hour12: false,
		};

		dateTimeObj["time"] = new Intl.DateTimeFormat(
			"de-AT",
			timeOptions
		).format(date);

		return dateTimeObj;
	};

	const getEventInfo = () => {
		let formattedStartDate = null;
		let formattedStartTime = null;
		let formattedEndDate = null;
		let formattedEndTime = null;

		if (startDate) {
			const startDateObj = new Date(startDate);
			const startDateTimeObj = getFormattedDateTime(startDateObj);

			if (startDateTimeObj) {
				formattedStartDate = startDateTimeObj.date;
				formattedStartTime = startDateTimeObj.time;
			}
		}

		if (endDate) {
			const endDateObj = new Date(endDate);
			const startDateTimeObj = getFormattedDateTime(endDateObj);

			if (startDateTimeObj) {
				formattedEndDate = startDateTimeObj.date;
				formattedEndTime = startDateTimeObj.time;
			}
		}

		const eventIsMultipleDays =
			formattedStartDate != formattedEndDate && endDate !== null;

		let eventInfoString = "";

		if (startDate) {
			if (eventIsMultipleDays) {
				eventInfoString = `${formattedStartDate} - ${formattedEndDate}`;
			} else {
				eventInfoString = `${formattedStartDate} | ${formattedStartTime} - ${formattedEndTime}`;
			}
		}

		if (onlineLocation) {
			eventInfoString = `${eventInfoString} | ${onlineLocation}`;
		}

		if (startDate || endDate) {
			return (
				<div className="search-card-event-info">
					<p className="caption">{eventInfoString}</p>
				</div>
			);
		} else {
			return;
		}
	};

	if (isLoading) {
		return (
			<div className="search-card w-full lg:w-1/2 p-3.5 md:p-4">
				<div className="card-element-landscape loading-card">
					<div className="card-element-landscape-actions">
						<div className="search-card-actions">
							<div className="search-card-loading-tags"></div>
						</div>
					</div>
					<a href={url} className="card-element-landscape-inner">
						<div className="card-element-landscape-img">
							<div className="search-card-product-image search-card-loading-image"></div>
						</div>
						<div className="card-element-landscape-content">
							<div className="card-element-landscape-header">
								<div className="w-full card-element-landscape-tags">
									<div className="search-card-tags">
										<div className="search-card-loading-tags"></div>
									</div>
								</div>
							</div>
							<h2 className="card-element-landscape-title h4-look">
								<div className="search-card-loading-headline"></div>
							</h2>
							<div className="card-element-landscape-text">
								<div className="search-card-loading-text"></div>
								<div className="search-card-loading-text"></div>
								<div className="search-card-loading-text"></div>
								<div className="search-card-loading-text-half"></div>
							</div>
						</div>
					</a>
				</div>
			</div>
		);
	}

	const handleClickBookmark = async () => {
		if (id) {
			if (isBookmarked) {
				setIsBookmarked(false);
				ToastStore.removeFromBookmark(
					image ?? "",
					Globals.wishlistUrl,
					fallbackImage ?? "",
					isEvent ? "event" : "product"
				);
				await removeFromBookmarks(id);
			} else {
				setIsBookmarked(true);
				ToastStore.addToBookmark(
					image ?? "",
					Globals.wishlistUrl,
					fallbackImage ?? "",
					isEvent ? "event" : "product"
				);
				await addToBookmarks(id);
			}
		}
	};

	const handleClickCart = () => {
		if (id && canAddToCart && purchasableId) {
			const data: IAddToCartData = {
				title: title || "",
				imageUrl: image || fallbackImage || "",
				productType: chips ? chips.join(", ") : "",
				amount: 1,
				price: price ?? 0,
				priceAsCurrency: priceAsCurrency ?? "",
				lineItems: 0,
			};

			try {
				updateCart(purchasableId, 1)
					.then((resp: any) => {
						if (resp.cart) {
							const cart: ICart = resp.cart;

							if (cart.lineItems) {
								data.lineItems = cart.lineItems.reduce(
									(prevVal, currentValue) =>
										prevVal + currentValue.qty,
									0
								);
							}
						}
					})
					.finally(() => {
						if (trackingData) {
							trackAddToCart(trackingData, 1);
						}
						ToastStore.addToCart(data);
					});
			} catch (error) {
				console.log(error);
			}
		}
	};

	let landscapeInnerClass = "card-element-landscape-inner";
	if (isTeachingSeries) {
		landscapeInnerClass = `${landscapeInnerClass} teaching-series`;
	}

	// console.log("isdigtialproduct ");
	// console.log(isDigitalProduct);

	const chipsColor = () => {
		switch (elementType) {
			case "events":
				return "bg-tertiary text-white";
			case "teachingSeries":
				return "bg-accentDark text-white";
			case "magazineArticle":
				return "bg-primary text-white";
			case "apaArticle":
				return "bg-primary text-white";
			default:
				return "bg-colorVeryLight text-body-dark1";
		}
	};

	const cardBgColor = () => {
		switch (elementType) {
			case "events":
				return "bg-tertiaryShade3 ";
			case "magazineArticle":
				return "bg-primaryShade3";
			case "apaArticle":
				return "bg-primaryShade3";
            case "pages":
                return "bg-secondaryShade1";
            case "schoolTypeLandingPage":
                return "bg-secondaryShade1";
			default:
				return "bg-white";
		}
	};

	return (
		<div className={`search-card w-full p-3.5 md:p-4 ${apaArticleUserMenu ? "lg:w-1/1" : "lg:w-1/2"}`}>
			<div className={`card-element-landscape ${cardBgColor()} ${isLandingPage ? "flex" : ""}`} style={{backgroundColor: cardBackgroundColor}}>
				<div className="card-element-landscape-actions">
					{downloadUrl && productCategory !== "LINK" && (
						<a
							className="additional-action"
							href={downloadUrl}
							download
							target="_blank"
							rel="noreferrer"
							title="Herunterladen"
						>
							<SVG
								style={{
									width: 24,
									marginLeft: 16,
									color: "#696969",
								}}
								icon="download"
								ariaLabel="Herunterladen"
							/>
						</a>
					)}
					{productCategory === "LINK" && (
						<a
							className="additional-action"
							href={url}
							target="_blank"
							rel="noreferrer"
						>
							<SVG
								style={{ width: 24, marginLeft: 16 }}
								icon="openLinkDarkGrey"
							/>
						</a>
					)}

					{id && canAddToCart && (
						<button onClick={() => handleClickCart()}>
							<SVG
								style={{ width: 24, marginLeft: 16 }}
								icon="cartDarkGrey"
								ariaLabel="Warenkorb"
							/>
						</button>
					)}
					{id && !isLandingPage && (
						<button
							onClick={() => void handleClickBookmark()}
							className="ml-4 bookmark js-product-boomark"
						>
							<SVG
								style={{ width: 24 }}
								icon={
									isBookmarked
										? "bookmarkActive"
										: "bookmarkDarkGrey"
								}
								ariaLabel="Merkliste"
							/>
						</button>
					)}
				</div>
				<a href={url} className={`${landscapeInnerClass} ${isLandingPage ? "flex flex-col justify-center relative" : ""}`}>
                    <div
                        className={`card-element-landscape-img ${
                            isMagazine || isEvent || isApaArticle || apaArticleUserMenu
                                ? "card-element-landscape-full-img"
                                : ""
                        } ${isLandingPage ? "opacity-0" : ""}`}
                    >
                        {image && <img src={image} alt={title} />}
                        {!image && (
                            <FallbackImage
                                style={{ marginRight: "1.5rem" }}
                                type={fallbackImage || null}
								ariaLabel={title}
                            ></FallbackImage>
                        )}
                    </div>
                    {!isLandingPage && (
                        <>
                            <div className="flex flex-col justify-between card-element-landscape-content">
                                <div>
                                    <div className="card-element-landscape-header">
                                        <div className="w-full card-element-landscape-tags">
                                            {/* {eventCategories &&
                                                eventCategories.map((category, index) => (
                                                    <span
                                                        key={`event-categories-${index}-${id}`}
                                                        className="tag search-card-event-category-tag bg-tertiary"
                                                    >
                                                        {category.title}
                                                    </span>
                                                ))} */}
                                            {chips && Array.isArray(chips) && chips?.map((chip) => (
                                                <span
                                                    key={chip}
                                                    className={`tag ${chipsColor()}`}
                                                >
                                                    {chip}
                                                </span>
                                            ))}
                                            {isTeachingSeries && (
                                                <span className={`tag ${chipsColor()}`}>
                                                    Reihe
                                                </span>
                                            )}
                                            {isMagazine && (
                                                <span className={`tag ${chipsColor()}`}>
                                                    öbv Magazin
                                                </span>
                                            )}
											{isApaArticle && (
                                                <span className={`tag ${chipsColor()}`}>
                                                    APA Artikel
                                                </span>
                                            )}
											{apaArticleUserMenu && (
                                                <span className="date">
                                                    Online bis: {endDate}
                                                </span>
                                            )}
                                            {isDigitalProduct && !isApp && (
                                                <span className="tag bg-secondary">
                                                    <span style={{ marginRight: 8 }}>
                                                        {t(
                                                            "my_oebv.digital_products.online_tag"
                                                        )}
                                                    </span>
                                                    <SVG icon="digitalProductSmall" ariaLabel={t("my_oebv.digital_products.online_tag")} />
                                                </span>
                                            )}
                                            {isApp && (
                                                <span className="tag bg-secondary">
                                                    <span>
                                                        {t(
                                                            "my_oebv.digital_products.app_tag"
                                                        )}
                                                    </span>
                                                </span>
                                            )}
                                            {productCategory && (
                                                <span className="tag bg-secondary">
                                                    {productCategory}{" "}
                                                    {icon && (
                                                        <SVG
                                                            style={{
                                                                width: 16,
                                                                marginLeft: 2,
                                                            }}
                                                            icon={icon}
                                                        />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {isNew ? (
                                        <div className="flex">
                                            <span className="text-body2 leading-body2 text-error">
                                                Neu
                                            </span>
                                            <h2 className="ml-2 card-element-landscape-title h4-look">
                                                {title}
                                            </h2>
                                        </div>
                                    ) : (
                                        <h2 className="card-element-landscape-title h4-look">
                                            {title}
                                        </h2>
                                    )}
                                    {subTitle && !isEvent && !isMagazine && (
                                        <h3 className={`card-element-landscape-subtitle text-body2 text-bodyDark2 leading-body2 mb-2 mt-2 ${isTeachingSeries ? "series-subtitle" : ""}`}>
                                            {subTitle}
                                        </h3>
                                    )}
                                    {subtitle && !isEvent && !isMagazine && (
                                        <h3 className={`card-element-landscape-subtitle text-body2 text-bodyDark2 leading-body2 mb-2 mt-2 ${isTeachingSeries ? "series-subtitle" : ""}`}>
                                            {subtitle}
                                        </h3>
                                    )}
                                    {isEvent && <>{getEventInfo()}</>}
                                    {!isDigitalAsset && !isTeachingSeries && (
                                        <p className="card-element-landscape-text search-card-element-landscape-text">
                                            {text}
                                        </p>
                                    )}
                                </div>

                                {isDigitalAsset && relatedProducts && (
                                    <p className="max-w-full text-bodyDark1 card-element-landscape-footer-text">
                                        <span className="block font-polo-medium">
                                            {t("search.card.matching_products")}
                                        </span>
                                        <span className="card-element-landscape-footer-text-inner">
                                            {relatedProducts.slice(0, 2).join(", ")}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                    {isLandingPage && (
                        <div className="absolute top-0 left-0 flex flex-col justify-center w-full h-full overflow-hidden text-center landing-page-card">
                            <img className="absolute object-cover w-full h-full" src={cardBackgroundImage ? cardBackgroundImage : "/dist/images/fallbacks/landing-page.svg"} alt="" />
                            <div className="px-6 md:px-8">
                                {subtitle && (
                                    <h2 className={`overline ${cardSubtitleColor ? "" : "text-secondaryDarker"}`} style={{color: cardSubtitleColor}}>{subtitle}</h2>
                                )}
                                <h3 className="h4-look" style={{color: "#ffffff"}}>{title}</h3>
                            </div>
                            {ctaButtonLinkText && (
                                <div>
                                    <Button buttonType="filled" text={ctaButtonLinkText} />
                                </div>
                            )}
                        </div>
                    )}
				</a>
			</div>
		</div>
	);
};

export default SearchCard;
